import * as React from 'react';
import { CancelRounded } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import { Subscription } from '@/shared/i18n';
import { SubscriptionEventKeys, SubscriptionEvents } from '../../events';
import { EventBus } from '@/shared/utils';
import { useSubscriptionStore } from '../../store/subscription-store';

interface SubscriptionDialogProps {
  eventBus: EventBus<SubscriptionEvents>;
  dict: Subscription;
}

const SubscriptionsDialog: React.FC<SubscriptionDialogProps> = ({ eventBus }) => {
  const open = useSubscriptionStore((state) => state.openSubscriptionModal);
  const toggleModal = useSubscriptionStore((state) => state.toggleOpenSubscriptionModal);
  const subscriptionsOptions = useSubscriptionStore((state) => state.subscriptionsOptions);
  const subscriptionPlan = useSubscriptionStore(
    (state) => state.customerSubscription?.subscriptionPlan,
  );
  const subscriptionStatus = useSubscriptionStore(
    (state) => state.customerSubscription?.subscriptionStatus,
  );
  const subscriptionSegment = useSubscriptionStore(
    (state) => state.customerSubscription?.subscriptionSegment,
  );
  const isNotRenewed =
    subscriptionStatus?.name === 'pending_cancellation' && subscriptionSegment?.name === 'Active';

  React.useEffect(() => {
    eventBus.emit(SubscriptionEventKeys.subscription.list, undefined);
  }, [eventBus]);

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={toggleModal}
      aria-labelledby="cambia-piano-dialog-title"
      aria-describedby="cambia-piano-dialog-description"
    >
      <DialogTitle id="cambia-piano-dialog-title" className="flex justify-between" component="div">
        <Typography variant="h5" component="h5">
          {subscriptionPlan ? 'Cambia' : 'Scegli'} il tuo piano
        </Typography>
        <CancelRounded className="text-action-divider cursor-pointer" onClick={toggleModal} />
      </DialogTitle>
      <DialogContent>
        {subscriptionsOptions ? (
          <>
            {subscriptionsOptions.map((option) => (
              <div
                key={option.name}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 w-full rounded-xl mb-4 bg-background"
              >
                <div className="flex flex-col sm:flex-row gap-x-6 sm:items-center gap-y-4">
                  <img
                    src={option.imgSrc}
                    className="w-full sm:max-w-[220px] md:w-[168px] h-auto rounded-lg overflow-hidden"
                    alt={option.name}
                  />
                  <div>
                    <Typography variant="s1" color="textPrimary" component="p">
                      {option.title}
                    </Typography>
                    <Typography variant="s1" color="textSecondary" component="p">
                      {option.price} €
                    </Typography>
                  </div>
                </div>
                {option.name === subscriptionPlan?.name ? (
                  isNotRenewed ? (
                    <Button
                      size="medium"
                      variant="outlined"
                      color="primary"
                      className="mr-auto sm:mr-0"
                      onClick={() =>
                        eventBus.emit(SubscriptionEventKeys.payment.openPortal.do, undefined)
                      }
                    >
                      <Typography variant="button-m">Rinnova piano</Typography>
                    </Button>
                  ) : (
                    <Chip
                      label={<Typography variant="button-s">Piano attuale</Typography>}
                      size="small"
                      color="default"
                      variant="outlined"
                      disabled
                      className="mr-auto sm:mr-0"
                    />
                  )
                ) : (
                  <Button
                    size="medium"
                    variant="outlined"
                    color="primary"
                    className="mr-auto sm:mr-0"
                    onClick={() =>
                      subscriptionPlan
                        ? eventBus.emit(SubscriptionEventKeys.subscription.update, option.priceId)
                        : eventBus.emit(SubscriptionEventKeys.subscription.create, {
                            priceId: option.priceId,
                            trialPeriodDays: option.trialPeriodDays,
                          })
                    }
                  >
                    <Typography variant="button-m">Scegli piano</Typography>
                  </Button>
                )}
              </div>
            ))}
          </>
        ) : (
          <CircularProgress size="3rem" color="primary" />
        )}
        {subscriptionPlan ? (
          <>
            <Typography variant="s1" color="textSecondary" component="p" gutterBottom>
              Altre Opzioni
            </Typography>
            <div className="flex flex-col justify-start sm:flex-row justify-between sm:items-center gap-4 rounded-xl py-6 px-4 bg-background">
              <div>
                <Typography variant="s1" component="p">
                  Termina iscrizione
                </Typography>
                <Typography variant="body-500" component="p" color="textSecondary">
                  Scegliendo questa opzione annulli il tuo abbonamento
                </Typography>
              </div>
              <Button
                onClick={() => eventBus.emit(SubscriptionEventKeys.subscription.cancel, undefined)}
                variant="outlined"
                size="medium"
                color="error"
                className="mr-auto sm:mr-0"
                disabled={isNotRenewed}
              >
                <Typography variant="button-m">Termina iscrizione</Typography>
              </Button>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionsDialog;
